import { Avatar } from "@suraasa/placebo-ui"
import { UserOrganizations } from "api/resources/growthspace/types"
import clsx from "clsx"
import { AuthManager } from "features/Authentication/AuthManager"
import { useState } from "react"
import { buildUserName } from "utils/helpers"
import { PropsWithClassName } from "utils/types"

export type DefaultLogoVariant = "orange" | "blue" | "green" | "purple"

const VARIANT_STYLES: Record<
  DefaultLogoVariant,
  { background: string; boxShadow: string; innerHighlight: string }
> = {
  orange: {
    background: "linear-gradient(180deg, #DC7706 37.22%, #FFCC34 78.78%)",
    boxShadow:
      "0 2px 1.8px 0 #B4750742, 0 4px 10.99px 0 #ECB96B, 0 15.98px 11.29px 0 #61461D26",
    innerHighlight: "inset 0 -6.37px 10.05px 2.12px #FFE2AD",
  },
  blue: {
    background: "linear-gradient(180deg, #1B73A3 43.22%, #49B7CC 80.46%)",
    boxShadow:
      "0 2px 1.8px 0 #078CB442, 0 4px 10.99px 0 #6BCEECB8, 0 15.98px 11.29px 0 #1D516126",
    innerHighlight: "inset 0 -6.37px 10.05px 2.12px #ADECFF",
  },
  green: {
    background:
      "radial-gradient(94.23% 94.23% at 50% 5.77%, #0F9173 19.8%, #97BC62 68.53%)",
    boxShadow:
      "0 2px 1.8px 0 #89B39142, 0 4px 10.99px 0 #308A3085, 0 15.98px 11.29px 0 #5A7F5926",
    innerHighlight: "inset 0 -6.37px 10.05px 2.12px #E6FFDF",
  },
  purple: {
    background: "linear-gradient(180deg, #8659B5 35.58%, #CD9CFF 100%)",
    boxShadow:
      "0 2px 1.8px 0 #4407B442, 0 4px 10.99px 0 #6332A080, 0 15.98px 11.29px 0 #B887EC24",
    innerHighlight: "inset 0 -6.37px 10.05px 2.12px #FFE0FD",
  },
}

const VARIANTS: DefaultLogoVariant[] = ["blue", "green", "purple", "orange"]

const getVariantFromName = (name: string): DefaultLogoVariant => {
  const normalized = name.trim().toLowerCase()
  const hash = [...normalized].reduce((sum, c) => sum + c.charCodeAt(0), 0)
  return VARIANTS[hash % VARIANTS.length]
}

type Props = PropsWithClassName<{
  name?: string
  variant?: DefaultLogoVariant
  size?: number
}>

const DefaultLogo = ({ name, variant, size = 30, className }: Props) => {
  const resolvedName = name ?? ""
  const v = variant ?? getVariantFromName(resolvedName)
  const styles = VARIANT_STYLES[v]
  const initial = resolvedName.trim().charAt(0).toUpperCase() || "?"

  return (
    <div
      className={clsx(
        "relative inline-flex shrink-0 items-center justify-center font-semibold text-[#F5F8EF]",
        className
      )}
      style={{
        width: size,
        height: size,
        borderRadius: "23.24%",
        border: "2px solid rgba(255,255,255,0.88)",
        boxShadow: styles.boxShadow,
        fontSize: size * 0.59,
        lineHeight: 1.3,
        textShadow: "0 2.5px 5px rgba(0,0,0,0.3)",
      }}
    >
      <span
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{ background: styles.background, borderRadius: "inherit" }}
      />
      <span
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{ borderRadius: "inherit", boxShadow: styles.innerHighlight }}
      />
      <span className="relative">{initial}</span>
    </div>
  )
}

export const GrowthSpaceImage = ({
  growthspace,
  className,
}: PropsWithClassName<{ growthspace?: UserOrganizations }>) => {
  const [failedLogoSrc, setFailedLogoSrc] = useState<string | null>(null)

  if (growthspace?.organization) {
    const { name, logo } = growthspace.organization
    if (logo && logo !== failedLogoSrc) {
      return (
        <img
          src={logo}
          alt={name}
          className={clsx("shrink-0 rounded-lg object-cover", className)}
          onError={() => setFailedLogoSrc(logo)}
        />
      )
    }
    return <DefaultLogo name={name} className={className} />
  }

  const authInfo = AuthManager.getPlatformAuth()
  const userName = authInfo ? buildUserName(authInfo.user) : undefined

  return (
    <Avatar
      className={className}
      name={userName}
      src={growthspace?.userPicture ?? undefined}
    />
  )
}

export default DefaultLogo
